/**
 * TikTok Streaming Service - Web Implementation
 * 
 * This service extends the unified StreamingPlatformService base class
 * and implements TikTok Live connection via EulerStream WebSocket API.
 * Ported from beemi-react-native for web compatibility.
 */

import { StreamingPlatformService } from '../base/StreamingPlatformService';
import {
  PlatformConnectionOptions,
  StreamValidationResult,
  StreamingComment,
  StreamingGift,
  StreamingLike,
  StreamingRoomInfo,
  StreamingMetrics,
  ConnectionState
} from '../../../types/streaming';
import { streamingConfigService } from '../StreamingConfigService';
import { MetricsTracker, parseStreamEvent } from '../../../lib/stream-parser';

// TikTok-specific types
export interface TikTokComment {
  id: string;
  username: string;
  comment: string;
  timestamp: number;
  type: 'comment';
  imageUrl?: string;
}

export interface TikTokStreamMetrics {
  viewerCount?: number;
  likeCount?: number;
  giftCount?: number;
  memberCount?: number;
  shareCount?: number;
  commentCount?: number;
}

export interface TikTokGift {
  id: string;
  username: string;
  giftName: string;
  giftCount: number;
  diamondCount?: number;
  totalDiamondCount?: number;
  timestamp: number;
  type: 'gift';
  imageUrl?: string;
  giftImageUrl?: string;
}

export interface TikTokLike {
  id: string;
  username: string;
  likeCount: number;
  timestamp: number;
  type: 'like';
}

export interface TikTokRoomInfo {
  isLive: boolean;
  isSharedScreen?: boolean;
  orientation?: 'portrait' | 'landscape';
  topic?: string;
  roomId?: string;
  streamerInfo?: {
    uniqueId: string;
    nickname: string;
    userId: string;
    profileImageUrl?: string;
    followerCount?: number;
  };
  rawData?: any;
}

export interface TikTokConnectionOptions {
  streamerUsername: string;
  onComment?: (comment: TikTokComment) => void;
  onGift?: (gift: TikTokGift) => void;
  onLike?: (like: TikTokLike) => void;
  onEvent?: (rawMessage: any) => void; // NEW: Unified raw event handler for stream-parser
  onMetricsUpdate?: (metrics: TikTokStreamMetrics) => void;
  onRoomInfo?: (roomInfo: StreamingRoomInfo) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
  onConnectionStatus?: (status: string) => void;
}

// EulerStream API interfaces
export interface EulerStreamUser {
  badges: any[];
  bioDescription: string;
  createTime: string;
  followInfo: any;
  nickname: string;
  profilePicture: { url?: string[]; urls?: string[] };
  secUid: string;
  uniqueId: string;
  userId: string;
  avatar_url?: string;
}

export interface EulerStreamMessage {
  type: string;
  data: any;
}

export class TikTokStreamingService extends StreamingPlatformService {
  private ws: WebSocket | null = null;
  private legacyOptions: TikTokConnectionOptions | null = null;
  private currentUsername: string = '';
  private processedGiftGroups = new Map<string, any>();
  private metricsUpdateInterval: ReturnType<typeof setInterval> | null = null;
  public tikTokRoomInfo: TikTokRoomInfo | null = null;
  private roomInfoResolve: ((value: TikTokRoomInfo) => void) | null = null;
  private roomInfoReject: ((reason: any) => void) | null = null;
  private roomInfoPromise: Promise<TikTokRoomInfo> | null = null;
  private hasReceivedInitialData = false;
  private metricsTracker: any; // MetricsTracker instance from stream-parser

  constructor() {
    super('tiktok');
    // Initialize metrics tracker for unified metrics tracking
    this.metricsTracker = new MetricsTracker();
  }

  /**
   * Override setConnectionState to use legacyOptions callbacks
   */
  protected setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    const callbacks = this.legacyOptions as TikTokConnectionOptions | undefined;
    
    switch (state) {
      case 'connected':
        this.reconnectAttempts = 0;
        callbacks?.onConnect?.();
        break;
      case 'disconnected':
        callbacks?.onDisconnect?.();
        break;
      case 'error':
        break;
    }
  }

  /**
   * Connect to TikTok stream
   */
  async connect(options: PlatformConnectionOptions | TikTokConnectionOptions): Promise<void> {
    if ('streamerUsername' in options) {
      return this.connectLegacy(options as TikTokConnectionOptions);
    }
    
    this.validateConnectionOptions(options as PlatformConnectionOptions);
    
    const legacyOptions: TikTokConnectionOptions = {
      streamerUsername: (options as PlatformConnectionOptions).username,
      onConnect: this.callbacks.onConnect,
      onDisconnect: this.callbacks.onDisconnect,
      onError: this.callbacks.onError,
      onConnectionStatus: this.callbacks.onConnectionStatus,
      onRoomInfo: this.callbacks.onRoomInfo,
      onComment: (comment) => this.handleLegacyComment(comment),
      onGift: (gift) => this.handleLegacyGift(gift),
      onLike: (like) => this.handleLegacyLike(like),
      onMetricsUpdate: (metrics) => this.handleLegacyMetrics(metrics)
    };

    return this.connectLegacy(legacyOptions);
  }

  /**
   * Legacy connect method for backward compatibility
   */
  private connectLegacy(options: TikTokConnectionOptions): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (this.isCleanedUp) {
        reject(new Error('Service has been cleaned up'));
        return;
      }

      // Get streaming config from backend
      const config = await streamingConfigService.getConfig();
      
      if (!config || !config.eulerStream.enabled) {
        reject(new Error('EulerStream is not enabled'));
        return;
      }

      if (!config.eulerStream.apiKey) {
        reject(new Error('EulerStream API key is missing'));
        return;
      }

      this.legacyOptions = options;
      this.reconnectAttempts = 0;
      this.connectionState = 'connecting';
      
      // Reset room info
      this.tikTokRoomInfo = null;
      this.roomInfoPromise = new Promise<TikTokRoomInfo>((resolveRoom, rejectRoom) => {
        this.roomInfoResolve = resolveRoom;
        this.roomInfoReject = rejectRoom;
      });

      // Clean username
      this.currentUsername = options.streamerUsername.replace(/^@/, '');
      const wsUrl = `${config.eulerStream.wsUrl}?uniqueId=${this.currentUsername}&apiKey=${config.eulerStream.apiKey}`;
      
      let isResolved = false;
      
      try {
        this.ws = new WebSocket(wsUrl);
      } catch (error) {
        reject(error);
        return;
      }

      let validationTimeout: ReturnType<typeof setTimeout>;

      this.ws.onopen = () => {
        console.log('🎬 [TikTok] WebSocket opened, waiting for initial data...');
        this.reconnectAttempts = 0;
        this.setConnectionState('connecting');
        this.startPeriodicMetricsSync();
        
        // Wait longer for initial data before resolving
        validationTimeout = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            // If we haven't received data after timeout, still resolve but keep connecting state
            if (!this.hasReceivedInitialData) {
              console.log('🎬 [TikTok] Timeout waiting for initial data');
              options.onConnectionStatus?.('Waiting for stream data...');
            }
            resolve();
          }
        }, 5000);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🎬 [TikTok] WebSocket message received:', data);
          this.handleMessage(data);
        } catch (error) {
          console.error('🎬 [TikTok] Failed to parse message:', error);
        }
      };

      this.ws.onclose = (event) => {
        const closeCode = event.code || 0;
        console.log('🎬 [TikTok] WebSocket closed - code:', closeCode, 'reason:', event.reason);
        
        if (validationTimeout) {
          clearTimeout(validationTimeout);
        }
        
        if (closeCode === 4404) {
          const errorMessage = 'User not live - try a different username';
          console.log('🎬 [TikTok] Error:', errorMessage);
          options.onConnectionStatus?.(errorMessage);
          
          if (!isResolved) {
            isResolved = true;
            reject(new Error(errorMessage));
          } else {
            options.onError?.(new Error(errorMessage));
          }
        }
        
        this.setConnectionState('disconnected');
        
        if (closeCode !== 4404 && closeCode !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          console.log('🎬 [TikTok] Attempting reconnection...');
          this.handleReconnection();
        }
      };

      this.ws.onerror = (error) => {
        if (validationTimeout) {
          clearTimeout(validationTimeout);
        }
        
        if (!isResolved) {
          isResolved = true;
          const errorObj = new Error('WebSocket connection error');
          this.handleError(errorObj);
          reject(errorObj);
        }
      };

      this.setConnectionTimeout();
    });
  }

  disconnect(): void {
    this.clearTimeouts();
    this.stopPeriodicMetricsSync();

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.legacyOptions = null;
    this.reconnectAttempts = 0;
    this.tikTokRoomInfo = null;
    this.processedGiftGroups.clear();
    this.hasReceivedInitialData = false;
    
    this.streamMetrics = {
      platform: 'tiktok',
      viewerCount: 0,
      likeCount: 0,
      giftCount: 0,
      memberCount: 0,
      shareCount: 0
    };
    
    this.roomInfoResolve = null;
    this.roomInfoReject = null;
    this.roomInfoPromise = null;
    
    this.setConnectionState('disconnected');
  }

  validateStreamRequirements(): StreamValidationResult {
    const roomInfo = this.tikTokRoomInfo;
    
    return {
      isMobileGaming: roomInfo?.isSharedScreen === true,
      isOtherGames: roomInfo?.topic?.toLowerCase().includes('other') === true,
      isPortrait: roomInfo?.orientation === 'portrait',
      isLive: roomInfo?.isLive === true && this.isConnected()
    };
  }

  // Public getters
  isStreamConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getRoomInfoLegacy(): TikTokRoomInfo | null {
    return this.tikTokRoomInfo;
  }

  getTikTokStreamMetrics(): TikTokStreamMetrics {
    return {
      viewerCount: this.streamMetrics.viewerCount,
      likeCount: this.streamMetrics.likeCount,
      giftCount: this.streamMetrics.giftCount,
      memberCount: this.streamMetrics.memberCount,
      shareCount: this.streamMetrics.shareCount,
      commentCount: this.streamMetrics.commentCount
    };
  }

  async waitForRoomInfo(timeout: number = 10000): Promise<TikTokRoomInfo> {
    if (this.tikTokRoomInfo) {
      return Promise.resolve(this.tikTokRoomInfo);
    }

    if (!this.roomInfoPromise) {
      return Promise.reject(new Error('Service not connected'));
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Timeout waiting for room info'));
      }, timeout);
    });

    return Promise.race([this.roomInfoPromise, timeoutPromise]);
  }

  getCurrentStreamerInfo(): { profileImageUrl?: string; nickname?: string; followerCount?: number } | null {
    if (!this.tikTokRoomInfo?.streamerInfo) {
      return null;
    }
    
    return {
      profileImageUrl: this.tikTokRoomInfo.streamerInfo.profileImageUrl,
      nickname: this.tikTokRoomInfo.streamerInfo.nickname,
      followerCount: this.tikTokRoomInfo.streamerInfo.followerCount
    };
  }

  // Legacy event handlers
  private handleLegacyComment(comment: TikTokComment): void {
    const unifiedComment: StreamingComment = {
      id: comment.id,
      username: comment.username,
      comment: comment.comment,
      timestamp: comment.timestamp,
      platform: 'tiktok',
      imageUrl: comment.imageUrl,
      metadata: { type: comment.type }
    };
    
    this.handleComment(unifiedComment);
  }

  private handleLegacyGift(gift: TikTokGift): void {
    const unifiedGift: StreamingGift = {
      id: gift.id,
      username: gift.username,
      giftName: gift.giftName,
      giftCount: gift.giftCount,
      value: gift.totalDiamondCount,
      timestamp: gift.timestamp,
      platform: 'tiktok',
      imageUrl: gift.imageUrl,
      giftImageUrl: gift.giftImageUrl,
      metadata: { 
        type: gift.type,
        diamondCount: gift.diamondCount,
        totalDiamondCount: gift.totalDiamondCount
      }
    };
    
    this.handleGift(unifiedGift);
  }

  private handleLegacyLike(like: TikTokLike): void {
    const unifiedLike: StreamingLike = {
      id: like.id,
      username: like.username,
      likeCount: like.likeCount,
      timestamp: like.timestamp,
      platform: 'tiktok',
      metadata: { type: like.type }
    };
    
    this.handleLike(unifiedLike);
  }

  private handleLegacyMetrics(metrics: TikTokStreamMetrics): void {
    const unifiedMetrics: StreamingMetrics = {
      platform: 'tiktok',
      viewerCount: metrics.viewerCount,
      likeCount: metrics.likeCount,
      giftCount: metrics.giftCount,
      memberCount: metrics.memberCount,
      shareCount: metrics.shareCount
    };
    
    this.updateMetrics(unifiedMetrics);
  }

  // Message handling
  private handleMessage(data: any) {
    if (!data || !this.legacyOptions) return;

    console.log('🎬 [TikTok] Processing message type:', data.type);

    if (data.data && (data.data.room_auth || data.data.live_type_screenshot !== undefined || data.data.owner)) {
      console.log('🎬 [TikTok] Parsing room info');
      this.parseRoomInfo(data.data);
      this.extractInitialMetrics(data.data);
    }

    if (data.messages && Array.isArray(data.messages)) {
      data.messages.forEach((message: EulerStreamMessage) => {
        this.processMessage(message);
      });
    } else {
      this.processMessage(data);
    }
  }

  private parseRoomInfo(roomData: any) {
    if (!roomData) return;

    const data = roomData.data || roomData;
    const roomInfo = data.roomInfo || data.room_info || {};
    const user = data.user || {};
    const rawData = data.raw?.data || {};
    const liveRoom = rawData.liveRoom || {};

    const isScreenSharing = liveRoom.liveRoomMode === 4;
    const topic = this.parseTopic({
      ...roomData,
      hashTagId: liveRoom.hashTagId,
      gameTagId: liveRoom.gameTagId
    });

    const profileImageUrl = user.avatarUrl ||
                           user.avatar_url ||
                           user.profilePicture?.urls?.[0] ||
                           user.profile_picture?.urls?.[0] ||
                           user.avatar?.urls?.[0] ||
                           undefined;

    const followerCount = user.followers || 0;

    this.tikTokRoomInfo = {
      isLive: roomInfo.isLive === true || roomInfo.is_live === true,
      isSharedScreen: isScreenSharing,
      orientation: 'portrait',
      topic: topic || 'other',
      roomId: roomInfo.id || String(roomInfo.id),
      streamerInfo: {
        uniqueId: user.unique_id || '',
        nickname: user.nickname || '',
        userId: user.numeric_uid || '',
        profileImageUrl: profileImageUrl,
        followerCount: followerCount
      },
      rawData: roomData
    };

    if (this.roomInfoResolve) {
      this.roomInfoResolve(this.tikTokRoomInfo);
      this.roomInfoResolve = null;
      this.roomInfoReject = null;
    }

    // Extract stream cover/thumbnail image from raw data (using liveRoom already declared above)
    const streamImageUrl = liveRoom.coverUrl || 
                           liveRoom.cover?.url_list?.[0] ||
                           data.cover || 
                           undefined;

    // Update unified room info
    const unifiedRoomInfo: StreamingRoomInfo = {
      platform: 'tiktok',
      isLive: this.tikTokRoomInfo.isLive,
      roomId: this.tikTokRoomInfo.roomId,
      channelId: this.currentUsername,
      title: this.tikTokRoomInfo.topic,
      isSharedScreen: this.tikTokRoomInfo.isSharedScreen,
      orientation: this.tikTokRoomInfo.orientation,
      topic: this.tikTokRoomInfo.topic,
      streamImageUrl: streamImageUrl,
      streamerInfo: this.tikTokRoomInfo.streamerInfo,
      rawData: this.tikTokRoomInfo.rawData
    };

    // Call both base class handler and legacy callback
    this.handleRoomInfo(unifiedRoomInfo);
    
    // Also call legacy callback directly (for when using TikTokConnectionOptions)
    if (this.legacyOptions?.onRoomInfo) {
      console.log('📡 [TikTok] Calling legacy onRoomInfo callback with:', {
        title: unifiedRoomInfo.title,
        streamImageUrl: unifiedRoomInfo.streamImageUrl,
        profileImageUrl: unifiedRoomInfo.streamerInfo?.profileImageUrl
      });
      this.legacyOptions.onRoomInfo(unifiedRoomInfo);
    }
    
    // Mark that we've received initial data
    this.markInitialDataReceived();
  }

  private parseTopic(roomData: any): string | undefined {
    let topic = roomData.topic || 
           roomData.category || 
           roomData.stream_category ||
           roomData.game_tag;
    
    if (!topic && (roomData.hashTagId === 5 || roomData.gameTagId === -1)) {
      topic = 'other games';
    }
  
    return topic;
  }

  private extractInitialMetrics(roomData: any) {
    if (!roomData || !this.legacyOptions) return;

    try {
      const userCount = roomData.user_count || roomData.stats?.user_count || 0;
      const likeCount = roomData.like_count || roomData.stats?.like_count || 0;
      
      console.log('🎬 [TikTok] Extracted metrics - viewers:', userCount, 'likes:', likeCount);
      
      if (userCount >= 0 || likeCount >= 0) {
        this.streamMetrics.viewerCount = userCount;
        this.streamMetrics.likeCount = likeCount;
        console.log('🎬 [TikTok] Calling onMetricsUpdate');
        this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
        
        // Mark that we've received initial data
        this.markInitialDataReceived();
      }
      
      // Initialize MetricsTracker with room data
      this.metricsTracker.initializeMetrics(roomData);
    } catch (error) {
      console.warn('Failed to extract initial metrics:', error);
    }
  }

  private markInitialDataReceived() {
    if (!this.hasReceivedInitialData && this.legacyOptions) {
      console.log('🎬 [TikTok] Initial data received, marking as fully connected');
      this.hasReceivedInitialData = true;
      this.setConnectionState('connected');
      this.legacyOptions.onConnectionStatus?.(`Connected to @${this.currentUsername}'s live stream! 🎬`);
    }
  }

  private startPeriodicMetricsSync() {
    if (this.metricsUpdateInterval) return;

    this.metricsUpdateInterval = setInterval(() => {
      this.requestRoomUpdate();
    }, 30000);
  }

  private stopPeriodicMetricsSync() {
    if (this.metricsUpdateInterval) {
      clearInterval(this.metricsUpdateInterval);
      this.metricsUpdateInterval = null;
    }
  }

  private requestRoomUpdate() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    try {
      const roomUpdateRequest = {
        type: 'room_update_request',
        timestamp: Date.now()
      };
      this.ws.send(JSON.stringify(roomUpdateRequest));
    } catch (error) {
      console.warn('Failed to request room update:', error);
    }
  }

  private processMessage(message: EulerStreamMessage | any) {
    if (!message || !this.legacyOptions) return;

    if (message.type === 'roomInfo' && message.data) {
      this.parseRoomInfo(message.data);
      return;
    }

    if (message.type === 'WebcastChatMessage' || message.type === 'chat' || message.comment) {
      // Pass FULL RAW Euler message - including type field!
      const commentData = message.data || message;
      
      if (commentData.comment && commentData.comment.trim()) {
        // Increment comment count for metrics
        this.streamMetrics.commentCount = (this.streamMetrics.commentCount || 0) + 1;
        
        // Send COMPLETE raw Euler message (not just .data!)
        this.legacyOptions.onComment?.(message);
        this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
        
        // Update metrics tracker
        const normalized = parseStreamEvent(message, 'tiktok');
        if (normalized) {
          const result = this.metricsTracker.processMessage(normalized);
          if (result.metricsChanged) {
            this.legacyOptions.onEvent?.({ type: 'stats', data: result.currentMetrics });
          }
        }
      }
    }
    else if (message.type === 'WebcastRoomUserSeqMessage') {
      const viewerCount = message.data?.viewerCount !== undefined 
        ? message.data.viewerCount 
        : (message.data?.totalUser || message.data?.total || 0);

      console.log('🎬 [TikTok] Viewer count update:', viewerCount);
      this.streamMetrics.viewerCount = viewerCount;
      this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
      
      // Update metrics tracker with periodic viewer count
      const normalized = {
        type: 'periodic',
        data: {
          viewerCount,
          totalViewers: message.data?.totalUser || message.data?.total || 0
        }
      };
      const result = this.metricsTracker.processMessage(normalized);
      if (result.metricsChanged) {
        this.legacyOptions.onEvent?.({ type: 'stats', data: result.currentMetrics });
      }
    }
    else if (message.type === 'WebcastGiftMessage') {
      const giftData = message.data || message;
      const giftGroupId = giftData.groupId || `single_${Date.now()}_${Math.random()}`;
      const currentRepeatCount = giftData.repeatCount || 1;

      const existingGiftData = this.processedGiftGroups.get(giftGroupId);
      let giftCountToSend: number;

      if (existingGiftData) {
        const existingRepeatCount = existingGiftData.repeatCount || 0;
        if (currentRepeatCount <= existingRepeatCount) {
          return;
        }
        giftCountToSend = currentRepeatCount - existingRepeatCount;
      } else {
        giftCountToSend = currentRepeatCount;
      }

      this.processedGiftGroups.set(giftGroupId, {
        repeatCount: currentRepeatCount,
        timestamp: Date.now()
      });

      // Pass COMPLETE raw Euler message, preserving type field
      this.processFinalGift(message, giftCountToSend, giftGroupId);
    }
    else if (message.type === 'WebcastLikeMessage') {
      // Pass FULL RAW Euler message - including type field!
      const likeData = message.data || message;
      
      // Update metrics for UI - simple approach: use totalLikeCount from TikTok
      // message.data structure: { likeCount: 2, totalLikeCount: 5258, ... }
      if (likeData.totalLikeCount > 0) {
        this.streamMetrics.likeCount = likeData.totalLikeCount;
      } else {
        // Fallback for platforms without totalLikeCount
        const incrementalCount = likeData.likeCount || likeData.count || 1;
        this.streamMetrics.likeCount = (this.streamMetrics.likeCount || 0) + incrementalCount;
      }
      
      // Send COMPLETE raw Euler message (not just .data!)
      this.legacyOptions.onLike?.(message);
      this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
      
      // Update metrics tracker
      const normalized = parseStreamEvent(message, 'tiktok');
      if (normalized) {
        const result = this.metricsTracker.processMessage(normalized);
        if (result.metricsChanged) {
          this.legacyOptions.onEvent?.({ type: 'stats', data: result.currentMetrics });
        }
      }
    }
    else if (message.type === 'WebcastMemberMessage') {
      this.streamMetrics.memberCount = (this.streamMetrics.memberCount || 0) + 1;
      this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
      
      // Pass raw member join event to unified handler
      this.legacyOptions.onEvent?.(message);
      
      // Update metrics tracker (join events don't directly affect tracked metrics)
      const normalized = parseStreamEvent(message, 'tiktok');
      if (normalized) {
        this.metricsTracker.processMessage(normalized);
      }
    }
    else if (message.type === 'room_update' || message.type === 'room_info_update') {
      const roomData = message.data || message;
      
      if (roomData) {
        const currentViewerCount = roomData.user_count || roomData.stats?.user_count;
        const currentLikeCount = roomData.like_count || roomData.stats?.like_count;
        
        let updated = false;
        
        if (currentViewerCount !== undefined && currentViewerCount !== this.streamMetrics.viewerCount) {
          this.streamMetrics.viewerCount = currentViewerCount;
          updated = true;
        }
        
        if (currentLikeCount !== undefined && currentLikeCount > (this.streamMetrics.likeCount || 0)) {
          this.streamMetrics.likeCount = currentLikeCount;
          updated = true;
        }
        
        if (updated) {
          this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
        }
      }
    }
    else if (message.type === 'WebcastShareMessage' || message.type === 'WebcastSocialMessage') {
      this.streamMetrics.shareCount = (this.streamMetrics.shareCount || 0) + 1;
      this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
      
      // Pass raw follow/share event to unified handler
      this.legacyOptions.onEvent?.(message);
      
      // Update metrics tracker
      const normalized = parseStreamEvent(message, 'tiktok');
      if (normalized) {
        const result = this.metricsTracker.processMessage(normalized);
        if (result.metricsChanged) {
          this.legacyOptions.onEvent?.({ type: 'stats', data: result.currentMetrics });
        }
      }
    }
  }

  private processFinalGift(fullMessage: any, giftCount: number, giftGroupId: string): void {
    if (!this.legacyOptions) return;

    // Update metrics for UI
    this.streamMetrics.giftCount = (this.streamMetrics.giftCount || 0) + giftCount;
    
    // Pass COMPLETE raw Euler message with updated repeatCount
    // Preserve the original message structure including type field!
    const messageWithCount = {
      ...fullMessage,
      data: {
        ...(fullMessage.data || {}),
        repeatCount: giftCount,
        _groupId: giftGroupId  // Add grouping info for context
      }
    };
    
    // Send COMPLETE raw Euler message - stream-parser will handle ALL parsing
    this.legacyOptions.onGift?.(messageWithCount);
    this.legacyOptions.onMetricsUpdate?.(this.getTikTokStreamMetrics());
    
    // Update metrics tracker
    const normalized = parseStreamEvent(messageWithCount, 'tiktok');
    if (normalized) {
      const result = this.metricsTracker.processMessage(normalized);
      if (result.metricsChanged) {
        this.legacyOptions.onEvent?.({ type: 'stats', data: result.currentMetrics });
      }
    }
  }

  protected async attemptReconnect(): Promise<void> {
    if (this.legacyOptions && !this.isCleanedUp) {
      return this.connectLegacy(this.legacyOptions);
    }
    throw new Error('No connection options available for reconnection');
  }
}

// Singleton instance
export const tikTokService = new TikTokStreamingService();

